import { notify } from '@/lib/toast';
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { formatCurrency, formatDate } from '@/lib/utils';
import { RotateCcw, Star, Check, X, Printer } from 'lucide-react';
import { printRefundSlip } from '@/lib/refund-slip';
import { PageHeader } from '@/components/ui/page-header';
import { Card } from '@/components/ui/card';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useTranslation } from 'react-i18next';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { RecordLink } from '@/components/records/RecordLink';
import { useRowOpen } from '@/components/records/useRowOpen';

const RETURN_TYPE_LABELS: Record<string, string> = {
  CASH_REFUND: 'Cash Refund',
  STORE_CREDIT: 'Store Credit',
  EXCHANGE: 'Exchange',
};

const RETURN_TYPE_STYLE: Record<string, string> = {
  CASH_REFUND: 'bg-info/10 text-info-text',
  STORE_CREDIT: 'bg-primary/10 text-primary-text',
  EXCHANGE: 'bg-warning/10 text-warning-text',
};

// POS-I79: refund tender (SalePayment.method) -> existing refund-method label key.
const REFUND_METHOD_KEYS: Record<string, [string, string]> = {
  CASH: ['refundMethodCash', 'Cash'],
  CARD: ['refundMethodCard', 'Card'],
  BANK_TRANSFER: ['refundMethodBank', 'Bank transfer'],
  MOBILE_WALLET: ['refundMethodWallet', 'Mobile wallet'],
  CREDIT: ['refundMethodCredit', 'Customer credit'],
  CHEQUE: ['refundMethodCheque', 'Cheque'],
  GIFT_CARD: ['refundMethodGiftCard', 'Gift card'],
  QR_CODE: ['refundMethodQr', 'QR code'],
};

const STATUS_LABELS: Record<string, string> = {
  PENDING_APPROVAL: 'Pending Approval',
  COMPLETED: 'Completed',
  REJECTED: 'Rejected',
};

const STATUS_STYLE: Record<string, string> = {
  PENDING_APPROVAL: 'bg-warning/10 text-warning-text',
  COMPLETED: 'bg-success/10 text-success-text',
  REJECTED: 'bg-destructive/10 text-destructive-text',
};

interface SaleReturnRow {
  id: string;
  saleId?: string;
  returnNumber: string;
  returnType: string;
  status: string;
  totalRefund: number | string;
  notes?: string | null;
  rejectionReason?: string | null;
  decidedByName?: string | null;
  createdAt: string;
  loyaltyPointsDeducted?: number | null;
  refundMethods?: string[];
  sale?: { invoiceNumber?: string; customer?: { name?: string; phone?: string } | null } | null;
}

export function ReturnsPage() {
  const rowOpen = useRowOpen();
  const { t } = useTranslation('sales');
  const { hasPermission } = useAuthStore();
  const canRead = hasPermission('pos:read') || hasPermission('pos:*');
  const canApprove = hasPermission('pos:delete') || hasPermission('pos:*');
  const showLoyalty = hasPermission('pos:*') || hasPermission('loyalty:*') || hasPermission('*');
  const [page, setPage] = useState(1);
  const [rejectTarget, setRejectTarget] = useState<SaleReturnRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['sale-returns', page],
    queryFn: () => api.get(`/sales/returns/all?page=${page}&limit=20`).then((r) => r.data),
    enabled: canRead,
  });

  const queryClient = useQueryClient();
  const handleReturnUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['sale-returns'] });
  }, [queryClient]);
  useSocketEvent('sale.refunded', handleReturnUpdate);
  useSocketEvent('sale.voided', handleReturnUpdate);

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.post(`/sales/returns/${id}/approve`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Return approved and refunded');
      queryClient.invalidateQueries({ queryKey: ['sale-returns'] });
    },
    onError: (err) => notify.error(err, 'Failed to approve return'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post(`/sales/returns/${id}/reject`, { reason }).then((r) => r.data),
    onSuccess: () => {
      toast.success('Return rejected');
      setRejectTarget(null);
      setRejectReason('');
      queryClient.invalidateQueries({ queryKey: ['sale-returns'] });
    },
    onError: (err) => notify.error(err, 'Failed to reject return'),
  });

  if (!canRead) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        You don't have permission to view returns.
      </div>
    );
  }

  // POS-I406: the Actions column is always there (Print slip for every reader).
  const colSpan = showLoyalty ? 11 : 10;

  const printSlip = async (id: string) => {
    try {
      if (!(await printRefundSlip(id))) toast.error('Allow pop-ups to print the refund slip');
    } catch (err) {
      notify.error(err, 'Failed to load the refund slip');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Sale Returns"
        subtitle="View and manage processed sale returns"
      />

      <Card className="animate-fade-in-up">
        <Table mobileCards>
            <THead>
              <Tr hover={false}>
                <Th>{t('tbl.return')}</Th>
                <Th>{t('originalSale')}</Th>
                <Th>{t('customer')}</Th>
                <Th>{t('tbl.type')}</Th>
                <Th>{t('tbl.refundedVia')}</Th>
                <Th>{t('status')}</Th>
                <Th>{t('tbl.refundAmount')}</Th>
                {showLoyalty && <Th>{t('tbl.ptsDeducted')}</Th>}
                <Th>{t('date')}</Th>
                <Th>{t('refundReasonLabel')}</Th>
                <Th className="text-end">{t('actions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {isLoading ? (
                <Tr hover={false} className="no-card"><Td colSpan={colSpan} className="p-0"><SkeletonTable rows={5} cols={colSpan} /></Td></Tr>
              ) : !data?.data?.length ? (
                <Tr hover={false} className="no-card"><Td colSpan={colSpan} className="p-0"><EmptyState icon={RotateCcw} title="No returns found" subtitle="Processed sale returns will appear here." /></Td></Tr>
              ) : data.data.map((ret: SaleReturnRow) => (
                <Tr key={ret.id} {...rowOpen('saleReturn', ret.id, ret.returnNumber)}>
                  <Td label={t('tbl.return')} className="font-mono text-xs text-foreground">
                    <RecordLink type="saleReturn" id={ret.id} label={ret.returnNumber} variant="mono" className="text-xs" />
                  </Td>
                  <Td label={t('originalSale')} className="font-mono text-xs text-foreground">
                    {ret.sale?.invoiceNumber ? (
                      <RecordLink type="invoice" id={ret.saleId} label={ret.sale.invoiceNumber} variant="mono" className="text-xs" />
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td label={t('customer')}>
                    {ret.sale?.customer?.name ? (
                      <div>
                        <p className="font-medium text-foreground">{ret.sale.customer.name}</p>
                        {ret.sale.customer.phone && (
                          <p className="text-xs text-muted-foreground">{ret.sale.customer.phone}</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Walk-in</span>
                    )}
                  </Td>
                  <Td label={t('tbl.type')}>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      RETURN_TYPE_STYLE[ret.returnType] ?? 'bg-muted text-muted-foreground'
                    }`}>
                      {RETURN_TYPE_LABELS[ret.returnType] ?? ret.returnType ?? '—'}
                    </span>
                  </Td>
                  <Td label={t('tbl.refundedVia')}>
                    {ret.refundMethods?.length ? (
                      ret.refundMethods
                        .map((m) => {
                          const [key, fallback] = REFUND_METHOD_KEYS[m] ?? [null, m];
                          return key ? t(key, { defaultValue: fallback }) : fallback;
                        })
                        .join(', ')
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </Td>
                  <Td label={t('status')}>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      STATUS_STYLE[ret.status] ?? 'bg-muted text-muted-foreground'
                    }`}>
                      {STATUS_LABELS[ret.status] ?? ret.status ?? '—'}
                    </span>
                  </Td>
                  <Td label={t('tbl.refundAmount')} className="font-medium text-foreground">{formatCurrency(Number(ret.totalRefund ?? 0))}</Td>
                  {showLoyalty && (
                    <Td label={t('tbl.ptsDeducted')}>
                      {ret.loyaltyPointsDeducted != null && ret.loyaltyPointsDeducted > 0 ? (
                        <span className="inline-flex items-center gap-1 text-destructive-text font-medium">
                          <Star className="h-3 w-3" />
                          −{ret.loyaltyPointsDeducted} pts
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </Td>
                  )}
                  <Td label={t('date')} className="text-muted-foreground">{formatDate(ret.createdAt)}</Td>
                  <Td label={t('refundReasonLabel')} className="text-muted-foreground max-w-xs truncate">{ret.status === 'REJECTED' ? (
                    <span title={ret.rejectionReason ?? undefined}>
                      <span className="text-destructive-text">Rejected{ret.decidedByName ? ` by ${ret.decidedByName}` : ''}: </span>
                      {ret.rejectionReason || 'no reason given'}
                    </span>
                  ) : (ret.notes ?? '—')}</Td>
                  <Td label={t('actions')} className="text-end" onClick={(e) => e.stopPropagation()}>
                      {ret.status === 'PENDING_APPROVAL' && canApprove ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => approveMutation.mutate(ret.id)}
                            disabled={approveMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-md bg-success-solid px-2.5 py-1 text-xs font-medium text-success-solid-foreground hover:bg-success-solid/90 disabled:opacity-50"
                          >
                            <Check className="h-3.5 w-3.5" /> Approve
                          </button>
                          <button
                            onClick={() => setRejectTarget(ret)}
                            disabled={rejectMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-md border border-destructive/30 px-2.5 py-1 text-xs font-medium text-destructive-text hover:bg-destructive/10 disabled:opacity-50"
                          >
                            <X className="h-3.5 w-3.5" /> Reject
                          </button>
                        </div>
                      ) : ret.status === 'COMPLETED' ? (
                        <button
                          onClick={() => printSlip(ret.id)}
                          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted/40"
                        >
                          <Printer className="h-3.5 w-3.5" /> Print slip
                        </button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                  </Td>
                </Tr>
              ))}
            </TBody>
        </Table>
        <div className="px-4 py-3 border-t border-border">
          <PaginationBar
            page={page}
            pageSize={20}
            total={data?.total ?? 0}
            onPageChange={setPage}
          />
        </div>
      </Card>

      <ConfirmDialog
        open={!!rejectTarget}
        title="Reject return?"
        message={`Reject return ${rejectTarget?.returnNumber ?? ''}? No refund will be issued and inventory will not be restocked.`}
        extra={
          <div className="mt-3 text-left">
            <label className="text-xs text-muted-foreground" htmlFor="reject-reason">Reason (shown to the cashier)</label>
            <input
              id="reject-reason"
              className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. goods used, outside return window"
            />
          </div>
        }
        confirmLabel="Reject"
        variant="danger"
        onCancel={() => { setRejectTarget(null); setRejectReason(''); }}
        onConfirm={() => rejectTarget && rejectMutation.mutate({ id: rejectTarget.id, reason: rejectReason.trim() || undefined })}
      />
    </div>
  );
}
